<template>
  <div class="clip-wrapper main-container">
    <player-rewind-buttons
      class="clip-buttons"
      :playing="videoPlay"
      @rewind="onRewind"
      @play="onPlay"
    />
    <section class="clip-settings">
      <div class="section-clip-title">
        <setting-icon/>
        <span>{{ $t('Settings') }}</span>
      </div>
      <div class="selected-settings">
        <span>{{ $t('Duration') }}</span>
        <select-component
          v-bind:selectedValue.sync="selectedDuration"
          :data="$options.durations"
          prop-for-value="value"
          prop-for-display="name"
        />
        <button-component
          class="start-time-btn"
          title="Start time"
          @btn-click="setStartTime"
          :contenthover="$t('CLIP.setStart')"
        />
        <input
          :value="convertTime(startTime)"
          class="form-control"
          :placeholder="$t('Enter time', {format: '(hh:mm:ss)'})"
          readonly
        />
      </div>
      <div class="clip-error" v-if="duration && !correctDuration">
        {{ $t('ERRORS.longDuration') }}
      </div>
    </section>
    <section class="clip-quick">
      <div class="section-clip-title">
        <scissors-icon/>
        <span>
          {{ $t('CLIP.tag') }}
        </span>
        <span class="placeholder-text">{{ $t('ATTRIBUTE.required') }}</span>
      </div>
      <div class="quick-names">
        <button-component
          v-for="tagName in buttonTags"
          :title="tagName"
          :key="tagName"
          :class="{
            'not-selected': tag && tag !== tagName,
          }"
          @btn-click="updateTag(tagName)"
        />
      </div>
      <div class="clip-error" v-if="!tag && showDisabledError">
        {{ $t('ERRORS.selectTag') }}
      </div>
    </section>
    <section class="clip-manual">
      <div class="clip-name">
        <label for="clip-name">{{ $t('CLIP.name') }}</label>
        <input
          v-model="name"
          id="clip-name"
          class="form-control"
          :placeholder="$t('PLACEHOLDER.Required')"
        />
        <button-component
          @btn-click="createClip"
          @disabled-click="displayError"
          title="CLIP.cut"
          :disabled="!canCut"
        />
      </div>
      <div v-if="!name.length && showDisabledError" class="clip-error">
        {{ $t('ERRORS.emptyName') }}
      </div>
      <div v-if="otherError" class="clip-error">
        {{ $t('ERRORS.somethingWrong') }}
      </div>
    </section>
    <created-clips
      :event="event"
      class="created-clips"
      @playClip="onClipPlay"
    />
    <tutorial-caller name="clipping"/>
  </div>
</template>

<script>
import PlayerRewindButtons from './PlayerRewindButtons';
import {PLAYER_ACTIONS} from '@/const';
import SettingIcon from '../../../assets/img/SettingIcon';
import SelectComponent from '../../../components/base-components/SelectComponent';
import ButtonComponent from '../../../components/base-components/buttons/ButtonComponent';
import {timeToHumanFormat} from '@/utils/helpers';
import ScissorsIcon from '../../../assets/img/ScissorsIcon';
import {SEND_CLIP} from '@/store/actionTypes';
import {mapActions, mapState, mapMutations} from 'vuex';
import CreatedClips from './CreatedClips';

import {ADD_TOAST} from '@/store/mutationTypes';
import VisaicV3Analytics from '@/components/base-components/Analytics/VisaicV3Analytics';
import TutorialCaller from '@/components/onboarding/TutorialCaller.vue';

export default {
  name: 'ClipCutComponent',
  components: {
    TutorialCaller,
    CreatedClips,
    ScissorsIcon,
    ButtonComponent,
    SelectComponent,
    SettingIcon,
    PlayerRewindButtons,
  },
  durations: [
    {
      value: 15,
      name: '15 seconds',
    },
    {
      value: 30,
      name: '30 seconds',
    },
    {
      value: 45,
      name: '45 seconds',
    },
    {
      value: 60,
      name: '60 seconds',
    },
    {
      value: 120,
      name: '120 seconds',
    },
    {
      value: 180,
      name: '180 seconds',
    },
  ],
  props: {
    playerInterface: {
      type: Object,
      required: true,
    },
    videoPlay: {
      type: Boolean,
      default: false,
    },
    event: {
      type: Object,
      required: true,
    },
    sportType: {
      type: String,
      default: 'default',
    },
    onClipCreatRequested: {
      type: Function,
    },
  },
  data() {
    return {
      startTime: 0,
      name: '',
      selectedDuration: this.$options.durations[1].value,
      duration: 0,
      showDisabledError: false,
      otherError: false,
      tag: null,
    };
  },
  methods: {
    ...mapActions({
      sendClip: SEND_CLIP,
    }),
    ...mapMutations({
      addToast: ADD_TOAST,
    }),
    convertTime(time) {
      return timeToHumanFormat(time);
    },
    onRewind(value) {
      this.playerInterface?.[PLAYER_ACTIONS.rewind]?.(value);
    },
    onPlay(play) {
      this.playerInterface?.[PLAYER_ACTIONS.play]?.(play);
    },
    setStartTime() {
      this.startTime = this.playerInterface?.[PLAYER_ACTIONS.getTime]?.();
    },
    updateTag(name) {
      this.tag = name;
    },
    onClipPlay({
      start,
      end,
    } = {}) {
      this.playerInterface?.[PLAYER_ACTIONS.playPart]?.({
        start,
        end,
      });
    },
    setInitData() {
      this.name = '';
      this.startTime = 0;
      this.selectedDuration = this.$options.durations[1].value;
      this.tag = null;
    },
    createClip() {
      this.sendClip({
        event: this.$route.params.id,
        name: this.name,
        start: timeToHumanFormat(this.startTime, true),
        duration: timeToHumanFormat(this.selectedDuration, true),
        tags: [this.tag],
      }).then(() => {
        this.setInitData();
        VisaicV3Analytics.event({
          event: 'clip_create',
          clip_id: this.$route.params.id,
        });
      }).catch(() => {
        this.otherError = true;
      }).finally(() => {
        this.onClipCreatRequested?.();
      });

      this.addToast({
        text: `CLIP.Hang`,
        time: 15000,
        type: 'success',
      });
    },
    displayError(display = true) {
      this.showDisabledError = display;
    },
    updateDuration() {
      this.duration = this.playerInterface?.[PLAYER_ACTIONS.totalTime]?.();
    },
  },
  watch: {
    name() {
      this.name.length && this.displayError(false);
      this.otherError = false;
    },
    startTime() {
      this.otherError = false;
    },
    selectedDuration() {
      this.otherError = false;
    },
    canCut(bool) {
      this.otherError = false;
      this.showDisabledError && this.displayError(!bool);
    },
    videoPlay() {
      this.updateDuration();
    },
  },
  computed: {
    ...mapState({
      settings: (state) => state.channel.settings,
    }),
    clipTags() {
      return this.settings?.clipping?.tags || [];
    },
    buttonTags() {
      return this.clipTags[this.sportType] ||
        [
          'Touchdown',
          'Interception',
          'Sack',
          'Big play',
          'Field Goal',
          'Tackle',
          'Overtime TD',
          'Other',
        ];
    },
    canCut() {
      return this.name.length && this.correctDuration && this.tag;
    },
    correctDuration() {
      return this.startTime + this.selectedDuration <= this.duration;
    },
  },
  mounted() {
    this.updateDuration();
  },
};
</script>

<style scoped lang="scss">
@import 'src/assets/css/mixins';

.clip-buttons {
  grid-area: buttons;
}

.clip-settings {
  grid-area: settings;
}

.clip-quick {
  grid-area: quick;
}

.clip-manual {
  grid-area: manual;
}

.created-clips {
  grid-area: created;
  max-width: 100vw;
}

.clip-wrapper {
  margin-top: 1rem;
  display: grid;
  gap: 3rem;
  justify-content: center;
  grid-template-areas:
    'buttons buttons'
    'settings created'
    'quick created'
    'manual created';

  @include media-max(md) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'buttons'
      'settings'
      'quick'
      'manual'
      'created';
  }

  label {
    margin-bottom: 0;
  }

  input {
    width: auto;
  }
}

.section-clip-title {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;

  .placeholder-text {
    font-weight: 400;
    text-transform: none;
    color: var(--settings-body-text-color-50);
  }
}

.selected-settings {
  display: flex;
  gap: 1rem;
  align-items: center;
  @include media-max(sm) {
    flex-wrap: wrap;
  }
}

.start-time-btn {
  white-space: nowrap;
}

.quick-names {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(4, minmax(9.5rem, 1fr));
  @include media-max(lg) {
    grid-template-columns: repeat(auto-fill, minmax(9.5rem, 1fr));
  }

  .not-selected {
    filter: grayscale(0.5) brightness(1.7);
  }
}

.clip-name {
  display: flex;
  gap: 1rem;
  align-items: center;
  @include media-max(xs) {
    flex-wrap: wrap;
  }
}

.clip-error {
  text-align: left;
  margin-top: 1rem;
  color: var(--theme-error);
}
</style>
